import React from 'react';
import { useTranslation } from 'react-i18next';
import { Quote } from 'lucide-react';
import theme from '../../../styles/theme';
import useInView from '../../../hooks/useInView';

const testimonials = [
  { key: 'a', initials: 'MR', name: 'María Rodríguez', store: 'Petlandia, CDMX' },
  { key: 'b', initials: 'JL', name: 'Jorge López', store: 'Huellitas Store, GDL' },
  { key: 'c', initials: 'SV', name: 'Sofía Vargas', store: 'Animalitos Felices, MTY' },
];

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [ref, visible] = useInView();

  const s = {
    section: {
      padding: `${theme.spacing['3xl']} ${theme.spacing.xl}`,
      backgroundColor: theme.colors.background.default,
    },
    inner: {
      maxWidth: '1120px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: theme.spacing['3xl'],
    },
    eyebrow: {
      display: 'inline-block',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: theme.colors.primary.main,
      marginBottom: theme.spacing.md,
    },
    h2: {
      fontSize: theme.typography.fluid.h2,
      fontWeight: theme.typography.fontWeight.bold,
      fontFamily: theme.typography.fontFamily.heading,
      color: theme.colors.text.primary,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: theme.spacing.xl,
    },
    card: (i, visible) => ({
      backgroundColor: theme.colors.background.paper,
      border: `1px solid ${theme.colors.border.light}`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      boxShadow: theme.shadows.sm,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(20px)',
      transition: `opacity 0.5s ease ${i * 100}ms, transform 0.5s ease ${i * 100}ms`,
    }),
    quoteIcon: {
      color: theme.colors.accent.main,
      marginBottom: theme.spacing.md,
    },
    quote: {
      fontSize: theme.typography.fontSize.base,
      color: theme.colors.text.secondary,
      lineHeight: theme.typography.lineHeight.relaxed,
      marginBottom: theme.spacing.xl,
      fontStyle: 'italic',
    },
    author: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.md,
    },
    avatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      backgroundColor: theme.colors.primary.light,
      color: theme.colors.primary.dark,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: theme.typography.fontWeight.bold,
      fontSize: theme.typography.fontSize.sm,
      flexShrink: 0,
    },
    name: {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text.primary,
    },
    storeName: {
      fontSize: theme.typography.fontSize.xs,
      color: theme.colors.text.secondary,
    },
  };

  return (
    <section style={s.section}>
      <div style={s.inner}>
        <div style={s.header}>
          <span style={s.eyebrow}>{t('public.testimonials.eyebrow', 'What our customers say')}</span>
          <h2 style={s.h2}>{t('public.testimonials.heading', 'Trusted by pet store owners')}</h2>
        </div>
        <div style={s.grid} ref={ref} className="testimonials-grid">
          {testimonials.map(({ key, initials, name, store }, i) => (
            <div key={key} style={s.card(i, visible)}>
              <Quote size={24} style={s.quoteIcon} />
              <p style={s.quote}>{t(`public.testimonials.${key}.quote`, '"Waggy transformed how we manage our store."')}</p>
              <div style={s.author}>
                <div style={s.avatar}>{initials}</div>
                <div>
                  <div style={s.name}>{name}</div>
                  <div style={s.storeName}>{store}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) { .testimonials-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
};

export default TestimonialsSection;
